/**
 * Hesaplanan (formül) form alanı — paylaşılan tipler + istemci değerlendirici + önizleme.
 * Formül = başlangıç operandı + sıralı adımlar (soldan-sağa değerlendirilir). Operand: başka alan (refKey ile)
 * veya sabit sayı. Sunucu (form.ts computeFormulaData) OTORİTER hesaplar; bu istemci sürümü canlı önizleme içindir.
 */
export type Operand = { kind: "field"; ref: string } | { kind: "const"; value: number };
export type FormulaStep = { op: "+" | "-" | "*" | "/"; operand: Operand };
export type Formula = { start: Operand; steps: FormulaStep[] };

/** Sunucu şemasıyla AYNA (form.ts FormulaSchema steps.max(50)) — aşılırsa mutasyonun tamamı reddedilir. */
export const MAX_FORMULA_STEPS = 50;
export const OP_SYMBOL: Record<FormulaStep["op"], string> = { "+": "+", "-": "−", "*": "×", "/": "÷" };
export const OP_OPTIONS: FormulaStep["op"][] = ["+", "-", "*", "/"];

/** Formülün referans verdiği refKey'ler (sabitler yoksayılır). */
export function formulaRefs(f: Formula): string[] {
  const refs: string[] = [];
  const add = (o: Operand) => { if (o.kind === "field") refs.push(o.ref); };
  add(f.start);
  for (const s of f.steps) add(s.operand);
  return refs;
}

type EvalField = { id: string; type: string; refKey?: string; formula?: Formula | null };

function toNum(v: unknown): number | null {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string" && v.trim() !== "") { const n = Number(v.replace(",", ".")); return Number.isFinite(n) ? n : null; }
  return null;
}

/**
 * Hesaplanan alanların değerlerini üret → { [computedFieldId]: number|null }.
 * Bağımlılıklar iteratif çözülür (hesaplanan alan başka hesaplanana bağlı olabilir); döngü/eksik girdi/0'a bölme → null.
 * Sunucudaki computeFormulaData ile aynı semantik (6 ondalık yuvarlama).
 */
export function computeFormulaValues(fields: EvalField[], data: Record<string, unknown>): Record<string, number | null> {
  const byRef = new Map<string, EvalField>();
  for (const f of fields) if (f.refKey) byRef.set(f.refKey, f);
  const computed = fields.filter((f) => f.type === "number" && f.formula);
  const out: Record<string, number | null> = {};
  const resolvedNum = (fieldId: string): number | null => (fieldId in out ? (out[fieldId] ?? null) : toNum(data[fieldId]));
  const pending = new Set(computed.map((f) => f.refKey));

  const operandVal = (o: Operand): number | null => {
    if (o.kind === "const") return Number.isFinite(o.value) ? o.value : null;
    const rf = byRef.get(o.ref);
    return rf ? resolvedNum(rf.id) : null;
  };
  const evalFormula = (formula: Formula): number | null => {
    let acc = operandVal(formula.start);
    if (acc == null) return null;
    for (const step of formula.steps) {
      const b = operandVal(step.operand);
      if (b == null) return null;
      if (step.op === "+") acc += b;
      else if (step.op === "-") acc -= b;
      else if (step.op === "*") acc *= b;
      else { if (b === 0) return null; acc /= b; }
    }
    return Number.isFinite(acc) ? Math.round(acc * 1e6) / 1e6 : null;
  };

  let progress = true;
  while (progress && pending.size) {
    progress = false;
    for (const f of computed) {
      if (!pending.has(f.refKey)) continue;
      const ready = formulaRefs(f.formula!).every((ref) => !pending.has(ref));
      if (!ready) continue;
      out[f.id] = evalFormula(f.formula!);
      pending.delete(f.refKey);
      progress = true;
    }
  }
  for (const f of computed) if (pending.has(f.refKey)) out[f.id] = null; // döngü
  return out;
}

/** Formülü insan-okur ifadeye çevir (soldan-sağa parantezli): "((Miktar × Fiyat) + 100)". */
export function formulaToString(f: Formula, labelOf: (ref: string) => string): string {
  const numFmt = (n: number) => new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 6 }).format(n);
  const operandStr = (o: Operand) => (o.kind === "const" ? numFmt(o.value) : (labelOf(o.ref) || "?"));
  let s = operandStr(f.start);
  for (const step of f.steps) s = `(${s} ${OP_SYMBOL[step.op]} ${operandStr(step.operand)})`;
  return s;
}
